/**
 * 대화방 꾸미기 카탈로그 (chat-room-style — 2026-08-26).
 *
 * **앱과 같은 id·같은 이름·같은 색**을 쓴다(iOS `RoomStyleCatalog` / `RoomBg.baseGradient` 미러).
 * 한쪽만 다른 색을 쓰면 폰에서 고른 '민트' 가 PC 에서 다른 민트로 열린다.
 *
 * 앱은 배경 위에 파티클(눈·비·별…)을 얹지만 여기서는 **그라데이션까지만** 옮긴다 —
 * 움직임은 대화를 읽는 데 방해가 되기 쉽고, 없어도 방을 알아보는 목적은 채워진다.
 * (앱에서 애니메이션 배경을 고른 방도 PC 에서는 같은 색의 정적 배경으로 열린다.)
 */

export interface RoomBackgroundPreset {
  id: string;
  label: string;
  /** 위 → 아래 두 색. 기본(none)은 없음. */
  gradient?: readonly [string, string];
  /** 어두운 배경 — 그 위 글자를 밝게 뒤집어야 한다. */
  dark?: boolean;
}

export const ROOM_BACKGROUNDS: readonly RoomBackgroundPreset[] = [
  { id: 'none', label: '기본' },
  { id: 'mint', label: '민트', gradient: ['#E7F8F1', '#CFF0E6'] },
  { id: 'peach', label: '피치', gradient: ['#FFF0E8', '#FFE0D2'] },
  { id: 'lavender', label: '라벤더', gradient: ['#F1ECFB', '#E4D9F7'] },
  { id: 'sky', label: '스카이', gradient: ['#E8F2FF', '#D2E4FB'] },
  { id: 'bubbles', label: '버블', gradient: ['#EAF3FF', '#DCEBFA'] },
  { id: 'aurora', label: '오로라', gradient: ['#0F172A', '#1E293B'], dark: true },
  { id: 'snow', label: '눈', gradient: ['#DDE7F2', '#C4D3E6'] },
  { id: 'hearts', label: '하트', gradient: ['#FFF0F5', '#FFE0EC'] },
  { id: 'rain', label: '비', gradient: ['#4A5A6B', '#33414F'], dark: true },
  { id: 'wind', label: '바람', gradient: ['#EAF7EE', '#D6EDDD'] },
  { id: 'stars', label: '별', gradient: ['#0B1026', '#1B2450'], dark: true },
  { id: 'robot', label: '로봇', gradient: ['#EAF2F6', '#D6E6EE'] },
  { id: 'balloons', label: '풍선', gradient: ['#FFF7EC', '#FFE9CC'] },
  { id: 'fireworks', label: '불꽃', gradient: ['#0A0A1A', '#161633'], dark: true },
];

export interface RoomAccentPreset {
  id: string;
  label: string;
  /** 내 말풍선 색. 기본(default)은 없음 — 테마 색을 그대로 쓴다. */
  hex?: string;
}

export const ROOM_ACCENTS: readonly RoomAccentPreset[] = [
  { id: 'default', label: '기본' },
  { id: 'blue', label: '블루', hex: '#3B82F6' },
  { id: 'purple', label: '퍼플', hex: '#8B5CF6' },
  { id: 'teal', label: '틸', hex: '#14B8A6' },
  { id: 'orange', label: '오렌지', hex: '#F97316' },
  { id: 'green', label: '그린', hex: '#10B981' },
  { id: 'pink', label: '핑크', hex: '#EC4899' },
  { id: 'red', label: '레드', hex: '#EF4444' },
];

export function backgroundPreset(id: string | null | undefined): RoomBackgroundPreset | null {
  if (!id || id === 'none') return null;
  // 모르는 id(앱이 먼저 새 배경을 낸 경우, 'custom' 등)는 기본으로 떨어뜨린다 — 화면이 깨지지 않는다.
  return ROOM_BACKGROUNDS.find((b) => b.id === id && b.gradient) ?? null;
}

export function accentHex(id: string | null | undefined): string | null {
  if (!id || id === 'default') return null;
  return ROOM_ACCENTS.find((a) => a.id === id)?.hex ?? null;
}

/** CSS 배경 값. 배경이 없으면 null(호출부가 기존 배경을 그대로 둔다). */
export function backgroundCss(id: string | null | undefined): string | null {
  const p = backgroundPreset(id);
  if (!p?.gradient) return null;
  return `linear-gradient(to bottom, ${p.gradient[0]}, ${p.gradient[1]})`;
}
